import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Home, Users, Briefcase, Clock, FileText, Bell, Settings, LogOut, Menu, X,
  ChevronRight, ChevronDown, Plus, CheckCircle, AlertCircle, MapPin, Calendar, User,
  ClipboardList, BarChart3, Play, Square, RefreshCw, Search, Filter, Eye,
  Truck, Camera, Upload, Image, Wrench, Shield, FileImage, Trash2, Save,
  Building2, Sliders, Key, MessageSquare, Send, Car, Fuel, Video, HelpCircle,
  ChevronLeft, Download, Edit3, Check, ArrowLeft, Edit2, UserCheck, Archive, Receipt, BookOpen, Box, Zap, HardHat
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { Badge, Button, Card, Input, Modal, Select, Spinner, Textarea, FileUploadInput } from "../../shared/ui";
import { DatePicker, DateTimePicker } from "../DateTimePicker";
import PDFSignatureFieldPlacer from "../PDFSignatureFieldPlacer";
import PDFDocumentSigner from "../PDFDocumentSigner";
import JobRecordsPanel from "./JobRecordsPanel";
import JobUploadsSection from "./JobUploadsSection";
import SiteCrewPanel from "../SiteCrewPanel";
import DocumentsView from "./DocumentsView";
import EquipmentScheduleSection from "./EquipmentScheduleSection";
import SWMSSignView from "./SWMSSignView";
import SWMSManageView from "./SWMSManageView";
import DailyRosterSection from "./DailyRosterSection";
import SigningDocumentsView from "./SigningDocumentsView";
import JobRegisterTab from "../JobRegisterTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobModal = ({ isOpen, onClose, job, onSave, canEdit }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    client_id: "",
    client_name: "",
    location: "",
    start_date: "",
    end_date: "",
    description: "",
    equipment_required: [],
    safety_requirements: [],
    job_plan: "",
    status: "scheduled",
    contract_amount: ""
  });
  const [loading, setLoading] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [showRoster, setShowRoster] = useState(false);
  const [showSWMS, setShowSWMS] = useState(false);
  const [showForms, setShowForms] = useState(false);
  const [jobForms, setJobForms] = useState({ total: 0, submissions: [] });
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [swmsStatus, setSwmsStatus] = useState(null);
  const [showSignSWMS, setShowSignSWMS] = useState(false);
  
  // Machine deployment state
  const [showMachines, setShowMachines] = useState(false);
  const [showSigningDocs, setShowSigningDocs] = useState(false);
  const [showFormAllocations, setShowFormAllocations] = useState(false);
  const [formAllocations, setFormAllocations] = useState([]);
  const [formTemplates, setFormTemplates] = useState([]);
  const [showAddAllocation, setShowAddAllocation] = useState(false);
  const [newAllocation, setNewAllocation] = useState({ template_id: '', signing_frequency: 'first_clock_in', completion_mode: 'supervisor_fills_team_signs', required_roles: [], all_employees_must_sign: false });
  const [signingDocuments, setSigningDocuments] = useState([]);
  const [showAddSigningDoc, setShowAddSigningDoc] = useState(false);
  const [allMachines, setAllMachines] = useState([]);
  const [activeDeployments, setActiveDeployments] = useState([]);
  const [deploymentHistory, setDeploymentHistory] = useState([]);
  const [showDeployForm, setShowDeployForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [deploymentForm, setDeploymentForm] = useState({
    machine_id: "",
    operator_id: "",
    ownership_type: "owned",
    hire_company: "",
    hire_daily_rate: "",
    hire_weekly_rate: "",
    start_date: new Date().toISOString().split('T')[0],
    start_time: "",
    expected_end_date: "",
    notes: ""
  });
  const [endDeploymentData, setEndDeploymentData] = useState(null);
  const [selectedDeploymentDetail, setSelectedDeploymentDetail] = useState(null);

  useEffect(() => {
    if (job) {
      setFormData({
        title: job.title || "",
        client_id: job.client_id || "",
        client_name: job.client_name || "",
        location: job.location || "",
        start_date: job.start_date || "",
        end_date: job.end_date || "",
        description: job.description || "",
        equipment_required: job.equipment_required || [],
        equipment_schedule: job.equipment_schedule || [],
        safety_requirements: job.safety_requirements || [],
        job_plan: job.job_plan || "",
        status: job.status || "scheduled",
        manager_id: job.manager_id || "",
        contract_amount: job.contract_amount != null ? String(job.contract_amount) : "",
        gps_required: job.gps_required || false,
        site_latitude: job.site_latitude || "",
        site_longitude: job.site_longitude || "",
        geofence_radius: job.geofence_radius || 100
      });
      setAssignedUsers(job.assigned_users || []);
    } else {
      setFormData({
        title: "",
        client_id: "",
        client_name: "",
        location: "",
        start_date: "",
        end_date: "",
        description: "",
        equipment_required: [],
        equipment_schedule: [],
        safety_requirements: [],
        job_plan: "",
        status: "scheduled",
        manager_id: "",
        contract_amount: "",
        gps_required: false,
        site_latitude: "",
        site_longitude: "",
        geofence_radius: 100
      });
      setAssignedUsers([]);
    }
    // Reset modal states when job changes
    setShowAssign(false);
    setShowDocuments(false);
    setShowRoster(false);
    setShowSWMS(false);
    setShowSignSWMS(false);
    setShowReport(false);
    setJobReport(null);
    setShowRecords(false);
    setJobRecords(null);
  }, [job]);

  // Job Report state
  const [showReport, setShowReport] = useState(false);
  const [jobReport, setJobReport] = useState(null);

  // Job Records bundle state (receipts / forms / dockets / photos for this job)
  const [showRecords, setShowRecords] = useState(false);
  const [showEquipReg, setShowEquipReg] = useState(false);
  const [showElecReg, setShowElecReg] = useState(false);
  const [jobRecords, setJobRecords] = useState(null);
  const [loadingRecords, setLoadingRecords] = useState(false);

  const fetchJobRecords = async () => {
    if (!job) return;
    setLoadingRecords(true);
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/records`);
      setJobRecords(res.data);
    } catch (e) { console.error(e); } finally { setLoadingRecords(false); }
  };

  const canViewReport = ['admin', 'manager', 'office_staff'].includes(user?.role);

  useEffect(() => {
    if (isOpen && canEdit) {
      fetchUsers();
      fetchClients();
    }
    if (isOpen && job) {
      fetchDocuments();
      fetchSigningDocuments();
      fetchSWMSStatus();
      fetchJobMachines();
      fetchAllMachines();
      fetchJobForms();
      fetchFormAllocations();
      fetchFormTemplates();
    }
  }, [isOpen, canEdit, job]);

  const fetchFormAllocations = async () => {
    if (!job) return;
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/form-allocations`);
      setFormAllocations(res.data);
    } catch (e) { console.error('Failed to fetch form allocations:', e); }
  };

  const fetchFormTemplates = async () => {
    try {
      const res = await axios.get(`${API}/forms/templates`);
      setFormTemplates(res.data);
    } catch (e) { console.error('Failed to fetch form templates:', e); }
  };

  const addFormAllocation = async () => {
    if (!newAllocation.template_id) {
      alert('Pick a form template first, mate.');
      return;
    }
    try {
      await axios.post(`${API}/jobs/${job.id}/form-allocations`, newAllocation);
      setShowAddAllocation(false);
      setNewAllocation({ template_id: '', signing_frequency: 'first_clock_in', completion_mode: 'supervisor_fills_team_signs', required_roles: [], all_employees_must_sign: false });
      await fetchFormAllocations();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to allocate form');
    }
  };

  const removeFormAllocation = async (id) => {
    if (!window.confirm('Remove this form allocation?')) return;
    try {
      await axios.delete(`${API}/jobs/${job.id}/form-allocations/${id}`);
      await fetchFormAllocations();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to remove allocation');
    }
  };

  const fetchJobForms = async () => {
    if (!job) return;
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/forms`);
      setJobForms(res.data);
    } catch (e) {
      console.error('Failed to fetch job forms:', e);
    }
  };

  const fetchJobReport = async () => {
    if (!job) return;
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/report`);
      setJobReport(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (e) {}
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch (e) {}
  };

  const fetchAllMachines = async () => {
    try {
      const res = await axios.get(`${API}/machines`);
      setAllMachines(res.data);
    } catch (e) {}
  };

  const fetchJobMachines = async () => {
    if (!job) return;
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/machines?include_ended=true`);
      setActiveDeployments(res.data.active_machines || []);
      setDeploymentHistory(res.data.deployment_history || []);
    } catch (e) {
      console.error('Failed to fetch job machines:', e);
    }
  };

  const fetchDocuments = async () => {
    if (!job) return;
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/documents`);
      setDocuments(res.data);
    } catch (e) {
      console.error('Failed to fetch documents:', e);
    }
  };

  const fetchSigningDocuments = async () => {
    if (!job) return;
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/signing-documents`);
      setSigningDocuments(res.data);
    } catch (e) {
      console.error('Failed to fetch signing documents:', e);
    }
  };

  const fetchSWMSStatus = async () => {
    if (!job) return;
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/swms-status`);
      setSwmsStatus(res.data);
      // If there's SWMS and user hasn't signed, show prompt (only for non-admin workers)
      if (res.data.has_swms && !res.data.signed && !canEdit) {
        setShowSignSWMS(true);
      }
    } catch (e) {
      console.error('Failed to fetch SWMS status:', e);
    }
  };

  // Machine deployment functions
  const handleDeployMachine = async (e) => {
    e.preventDefault();
    if (!deploymentForm.machine_id) {
      alert("Please select a machine to deploy");
      return;
    }
    try {
      const payload = {
        machine_id: deploymentForm.machine_id,
        operator_id: deploymentForm.operator_id || null,
        ownership_type: deploymentForm.ownership_type,
        hire_company: deploymentForm.ownership_type === "hired" ? deploymentForm.hire_company : null,
        hire_daily_rate: deploymentForm.ownership_type === "hired" && deploymentForm.hire_daily_rate ? parseFloat(deploymentForm.hire_daily_rate) : null,
        hire_weekly_rate: deploymentForm.ownership_type === "hired" && deploymentForm.hire_weekly_rate ? parseFloat(deploymentForm.hire_weekly_rate) : null,
        start_date: deploymentForm.start_date,
        start_time: deploymentForm.start_time || null,
        expected_end_date: deploymentForm.expected_end_date || null,
        notes: deploymentForm.notes || null
      };
      await axios.post(`${API}/jobs/${job.id}/machines/deploy`, payload);
      alert("Machine deployed successfully!");
      setShowDeployForm(false);
      setDeploymentForm({
        machine_id: "",
        operator_id: "",
        ownership_type: "owned",
        hire_company: "",
        hire_daily_rate: "",
        hire_weekly_rate: "",
        start_date: new Date().toISOString().split('T')[0],
        start_time: "",
        expected_end_date: "",
        notes: ""
      });
      fetchJobMachines();
      fetchAllMachines();
      onSave();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to deploy machine");
    }
  };

  const handleEndDeployment = async () => {
    if (!endDeploymentData) return;
    try {
      const payload = {
        end_date: endDeploymentData.end_date || new Date().toISOString().split('T')[0],
        end_time: endDeploymentData.end_time || null,
        reason: endDeploymentData.reason || "job_complete",
        notes: endDeploymentData.notes || null
      };
      await axios.post(`${API}/jobs/${job.id}/machines/${endDeploymentData.deployment_id}/end`, payload);
      alert("Deployment ended successfully!");
      setEndDeploymentData(null);
      fetchJobMachines();
      fetchAllMachines();
      onSave();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to end deployment");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Clean up data - convert empty strings to null for optional float fields
      const cleanData = {
        ...formData,
        site_latitude: formData.site_latitude === "" ? null : formData.site_latitude,
        site_longitude: formData.site_longitude === "" ? null : formData.site_longitude,
        manager_id: formData.manager_id === "" ? null : formData.manager_id,
        contract_amount: formData.contract_amount === "" || formData.contract_amount == null
          ? null
          : parseFloat(formData.contract_amount),
      };
      if (job) {
        await axios.put(`${API}/jobs/${job.id}`, cleanData);
      } else {
        await axios.post(`${API}/jobs`, cleanData);
      }
      onSave();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to save job");
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    try {
      await axios.post(`${API}/jobs/${job.id}/assign`, { user_ids: assignedUsers });
      alert("Users assigned successfully");
    } catch (e) {
      alert("Failed to assign users");
    }
  };

  const handleCompleteJob = async () => {
    // Pre-check: does the supervisor still owe any end-of-job forms?
    try {
      const check = await axios.post(`${API}/jobs/${job.id}/complete-check`);
      if (!check.data.ready_to_complete) {
        const names = check.data.missing_forms.map(f => `• ${f.name}`).join('\n');
        alert(`Hold up, mate — you've gotta provide these end-of-job forms before marking this job complete:\n\n${names}\n\nHead to the Forms tab on this job and fill them out.`);
        return;
      }
    } catch (e) {
      // Non-fatal; continue and let server re-validate
      console.warn('Could not pre-check completion readiness', e);
    }

    if (!window.confirm("Mark this job as completed? This will generate a completion report and notify Admin/Office staff.")) return;
    try {
      const res = await axios.post(`${API}/jobs/${job.id}/complete`);
      alert("Job marked as completed! Report generated and sent to Admin/Office.");
      setJobReport(res.data.report);
      setShowReport(true);
      onSave();
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed to complete job";
      if (typeof detail === 'string' && detail.startsWith('END_OF_JOB_FORMS_REQUIRED:')) {
        alert(`Mate, you've still got end-of-job forms to provide.\n\n${detail.split(':', 2)[1]}`);
      } else {
        alert(detail);
      }
    }
  };

  const handleCancelJob = async () => {
    if (!window.confirm("Are you sure you want to cancel this job? This action can be undone by editing the job status.")) return;
    try {
      await axios.put(`${API}/jobs/${job.id}`, { status: "cancelled" });
      alert("Job has been cancelled");
      onSave();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to cancel job");
    }
  };

  const handleDeleteJob = async () => {
    if (!window.confirm(`Are you sure you want to DELETE this job "${job.title}"? This will remove all associated data. This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/jobs/${job.id}`);
      alert("Job deleted successfully");
      onSave();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to delete job");
    }
  };

  const toggleUser = (userId) => {
    setAssignedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={job ? "Job Details" : "Create Job"} size="lg">
      {/* SWMS Sign Prompt for Workers */}
      {showSignSWMS && swmsStatus?.has_swms ? (
        <SWMSSignView 
          job={job}
          swms={swmsStatus.swms}
          signed={swmsStatus.signed}
          signature={swmsStatus.signature}
          onSign={() => { fetchSWMSStatus(); setShowSignSWMS(false); }}
          onBack={() => setShowSignSWMS(false)}
        />
      ) : showSWMS && canEdit ? (
        <SWMSManageView 
          job={job}
          onBack={() => setShowSWMS(false)}
          onRefresh={fetchSWMSStatus}
        />
      ) : showEquipReg ? (
        <div className="space-y-4" data-testid="job-equip-register-view">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold uppercase flex items-center gap-2">
              <Box className="w-5 h-5 text-green-600" /> Equipment Register — {job?.title}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setShowEquipReg(false)}>Back</Button>
          </div>
          <JobRegisterTab jobId={job?.id} kind="equipment" />
        </div>
      ) : showElecReg ? (
        <div className="space-y-4" data-testid="job-elec-register-view">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold uppercase flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" /> Electrical Register — {job?.title}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setShowElecReg(false)}>Back</Button>
          </div>
          <JobRegisterTab jobId={job?.id} kind="electrical" />
        </div>
      ) : showMachines ? (
        /* Machine Deployment View */
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold uppercase">Equipment Deployed - {job?.title}</h3>
            <Button variant="ghost" size="sm" onClick={() => { setShowMachines(false); setSelectedDeploymentDetail(null); setShowDeployForm(false); setEndDeploymentData(null); setShowHistory(false); }}>Back</Button>
          </div>

          {/* End Deployment Modal */}
          {endDeploymentData && (
            <Card className="bg-red-50 border-red-200">
              <h4 className="font-bold uppercase mb-4 text-red-800">End Deployment</h4>
              <p className="text-sm text-slate-600 mb-4">Remove <strong>{endDeploymentData.machine_name}</strong> from this job?</p>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <Input
                  label="End Date *"
                  type="date"
                  value={endDeploymentData.end_date || new Date().toISOString().split('T')[0]}
                  onChange={(e) => setEndDeploymentData({...endDeploymentData, end_date: e.target.value})}
                />
                <Input
                  label="End Time"
                  type="time"
                  value={endDeploymentData.end_time || ""}
                  onChange={(e) => setEndDeploymentData({...endDeploymentData, end_time: e.target.value})}
                />
              </div>
              <Select
                label="Reason"
                value={endDeploymentData.reason || "job_complete"}
                onChange={(e) => setEndDeploymentData({...endDeploymentData, reason: e.target.value})}
                options={[
                  { value: "job_complete", label: "Job Complete" },
                  { value: "no_longer_needed", label: "No Longer Needed" },
                  { value: "maintenance", label: "Requires Maintenance" },
                  { value: "relocated", label: "Relocated to Another Job" },
                  { value: "other", label: "Other" }
                ]}
                className="mb-4"
              />
              <Textarea
                label="Notes"
                value={endDeploymentData.notes || ""}
                onChange={(e) => setEndDeploymentData({...endDeploymentData, notes: e.target.value})}
                placeholder="Any notes about why machine is being removed..."
                className="mb-4"
              />
              <div className="flex gap-3">
                <Button variant="destructive" onClick={handleEndDeployment}>End Deployment</Button>
                <Button variant="ghost" onClick={() => setEndDeploymentData(null)}>Cancel</Button>
              </div>
            </Card>
          )}

          {/* Deploy Machine Form */}
          {showDeployForm && canEdit && (
            <Card className="bg-orange-50 border-orange-200">
              <h4 className="font-bold uppercase mb-4">Deploy Machine to Job</h4>
              <form onSubmit={handleDeployMachine} className="space-y-4">
                <Select
                  label="Select Machine *"
                  value={deploymentForm.machine_id}
                  onChange={(e) => setDeploymentForm({...deploymentForm, machine_id: e.target.value})}
                  options={[
                    { value: "", label: "-- Select a machine --" },
                    ...allMachines.filter(m => m.status === "available" || m.status === "in_use").map(m => ({
                      value: m.id,
                      label: `${m.name} (${m.machine_type?.replace('_', ' ')}) - ${m.status === 'available' ? 'Available' : 'In Use'}`
                    }))
                  ]}
                />
                <Select
                  label="Assign Operator"
                  value={deploymentForm.operator_id}
                  onChange={(e) => setDeploymentForm({...deploymentForm, operator_id: e.target.value})}
                  options={[
                    { value: "", label: "-- No operator assigned --" },
                    ...users.filter(u => ['crane_operator', 'rigger', 'labourer', 'supervisor'].includes(u.role)).map(u => ({
                      value: u.id,
                      label: `${u.first_name} ${u.last_name} (${u.role?.replace('_', ' ')})`
                    }))
                  ]}
                />
                <div className="grid md:grid-cols-2 gap-4">
                  <Select
                    label="Ownership Type"
                    value={deploymentForm.ownership_type}
                    onChange={(e) => setDeploymentForm({...deploymentForm, ownership_type: e.target.value})}
                    options={[
                      { value: "owned", label: "Company Owned" },
                      { value: "hired", label: "Hired / Rented" }
                    ]}
                  />
                  {deploymentForm.ownership_type === "hired" && (
                    <Input
                      label="Hire Company *"
                      value={deploymentForm.hire_company}
                      onChange={(e) => setDeploymentForm({...deploymentForm, hire_company: e.target.value})}
                      placeholder="e.g., ABC Crane Hire"
                      required={deploymentForm.ownership_type === "hired"}
                    />
                  )}
                </div>
                {deploymentForm.ownership_type === "hired" && (
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      label="Daily Rate ($)"
                      type="number"
                      value={deploymentForm.hire_daily_rate}
                      onChange={(e) => setDeploymentForm({...deploymentForm, hire_daily_rate: e.target.value})}
                    />
                    <Input
                      label="Weekly Rate ($)"
                      type="number"
                      value={deploymentForm.hire_weekly_rate}
                      onChange={(e) => setDeploymentForm({...deploymentForm, hire_weekly_rate: e.target.value})}
                    />
                  </div>
                )}
                <div className="grid md:grid-cols-3 gap-4">
                  <Input
                    label="Start Date *"
                    type="date"
                    value={deploymentForm.start_date}
                    onChange={(e) => setDeploymentForm({...deploymentForm, start_date: e.target.value})}
                    required
                  />
                  <Input
                    label="Start Time"
                    type="time"
                    value={deploymentForm.start_time}
                    onChange={(e) => setDeploymentForm({...deploymentForm, start_time: e.target.value})}
                  />
                  <Input
                    label="Expected End Date"
                    type="date"
                    value={deploymentForm.expected_end_date}
                    onChange={(e) => setDeploymentForm({...deploymentForm, expected_end_date: e.target.value})}
                  />
                </div>
                <Textarea
                  label="Notes"
                  value={deploymentForm.notes}
                  onChange={(e) => setDeploymentForm({...deploymentForm, notes: e.target.value})}
                  placeholder="Any deployment notes..."
                />
                <div className="flex gap-3">
                  <Button type="submit">Deploy Machine</Button>
                  <Button type="button" variant="ghost" onClick={() => setShowDeployForm(false)}>Cancel</Button>
                </div>
              </form>
            </Card>
          )}

          {/* Deployment Detail View */}
          {selectedDeploymentDetail && (
            <Card className="bg-blue-50 border-blue-200">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant={selectedDeploymentDetail.ownership_type === "hired" ? "warning" : "success"}>
                      {selectedDeploymentDetail.ownership_type === "hired" ? "Hired" : "Owned"}
                    </Badge>
                    <Badge variant={selectedDeploymentDetail.status === "active" ? "success" : "default"}>
                      {selectedDeploymentDetail.status === "active" ? "Active" : "Ended"}
                    </Badge>
                  </div>
                  <h4 className="text-xl font-bold">{selectedDeploymentDetail.machine_name}</h4>
                  <p className="text-sm text-slate-600 capitalize">{selectedDeploymentDetail.machine_type?.replace('_', ' ')}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedDeploymentDetail(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                {selectedDeploymentDetail.machine_rego && (
                  <div><span className="text-xs text-slate-500">Rego:</span> <span className="font-medium">{selectedDeploymentDetail.machine_rego}</span></div>
                )}
                {selectedDeploymentDetail.operator_name && (
                  <div><span className="text-xs text-slate-500">Operator:</span> <span className="font-medium">{selectedDeploymentDetail.operator_name}</span></div>
                )}
                <div><span className="text-xs text-slate-500">Start:</span> <span className="font-medium">{selectedDeploymentDetail.start_date} {selectedDeploymentDetail.start_time || ''}</span></div>
                {selectedDeploymentDetail.end_date && (
                  <div><span className="text-xs text-slate-500">End:</span> <span className="font-medium">{selectedDeploymentDetail.end_date}</span></div>
                )}
                {selectedDeploymentDetail.total_days && (
                  <div><span className="text-xs text-slate-500">Total Days:</span> <span className="font-medium">{selectedDeploymentDetail.total_days}</span></div>
                )}
                {selectedDeploymentDetail.ownership_type === "hired" && selectedDeploymentDetail.hire_company && (
                  <div><span className="text-xs text-slate-500">Hire Company:</span> <span className="font-medium">{selectedDeploymentDetail.hire_company}</span></div>
                )}
                {selectedDeploymentDetail.hire_daily_rate && (
                  <div><span className="text-xs text-slate-500">Daily Rate:</span> <span className="font-medium">${selectedDeploymentDetail.hire_daily_rate}</span></div>
                )}
                <div><span className="text-xs text-slate-500">Deployed By:</span> <span className="font-medium">{selectedDeploymentDetail.deployed_by_name}</span></div>
              </div>
              {selectedDeploymentDetail.notes && (
                <p className="mt-3 text-sm text-slate-600 bg-white p-2 rounded">{selectedDeploymentDetail.notes}</p>
              )}
              {canEdit && selectedDeploymentDetail.status === "active" && (
                <div className="mt-4">
                  <Button variant="destructive" size="sm" onClick={() => {
                    setEndDeploymentData({
                      deployment_id: selectedDeploymentDetail.id,
                      machine_name: selectedDeploymentDetail.machine_name,
                      end_date: new Date().toISOString().split('T')[0],
                      end_time: "",
                      reason: "job_complete",
                      notes: ""
                    });
                    setSelectedDeploymentDetail(null);
                  }}>
                    End Deployment
                  </Button>
                </div>
              )}
            </Card>
          )}

          {/* Active Deployments */}
          {!showDeployForm && !endDeploymentData && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold uppercase text-sm">Active Deployments ({activeDeployments.length})</h4>
                {canEdit && (
                  <Button size="sm" onClick={() => setShowDeployForm(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Deploy Machine
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {activeDeployments.map(d => (
                  <div 
                    key={d.id} 
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                      d.ownership_type === "hired" 
                        ? "bg-amber-50 border-amber-200 hover:bg-amber-100" 
                        : "bg-green-50 border-green-200 hover:bg-green-100"
                    }`}
                    onClick={() => setSelectedDeploymentDetail(d)}
                  >
                    <div className="flex items-center gap-3">
                      <Truck className={`w-5 h-5 ${d.ownership_type === "hired" ? "text-amber-600" : "text-green-600"}`} />
                      <div>
                        <p className="font-semibold">{d.machine_name}</p>
                        <p className="text-xs text-slate-500">
                          {d.operator_name ? `${d.operator_name} • ` : ''}{d.start_date}
                          {d.ownership_type === "hired" && d.hire_company ? ` • ${d.hire_company}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={d.ownership_type === "hired" ? "warning" : "success"}>
                        {d.ownership_type === "hired" ? "Hired" : "Owned"}
                      </Badge>
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={(e) => { 
                          e.stopPropagation(); 
                          setEndDeploymentData({
                            deployment_id: d.id,
                            machine_name: d.machine_name,
                            end_date: new Date().toISOString().split('T')[0],
                            end_time: "",
                            reason: "job_complete",
                            notes: ""
                          });
                        }}>
                          <Square className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {activeDeployments.length === 0 && (
                  <div className="text-center py-6 text-slate-500">
                    <Truck className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p>No machines currently deployed to this job</p>
                    {canEdit && (
                      <Button size="sm" className="mt-3" onClick={() => setShowDeployForm(true)}>
                        <Plus className="w-4 h-4 mr-1" /> Deploy First Machine
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Deployment History */}
          {!showDeployForm && !endDeploymentData && (
            <div>
              <button 
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center gap-2 font-bold uppercase text-sm mb-3 text-slate-600 hover:text-slate-900"
              >
                <ChevronRight className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-90' : ''}`} />
                Deployment History ({deploymentHistory.length})
              </button>
              {showHistory && (
                <div className="space-y-2">
                  {deploymentHistory.map(d => (
                    <div 
                      key={d.id} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100"
                      onClick={() => setSelectedDeploymentDetail(d)}
                    >
                      <div className="flex items-center gap-3">
                        <Truck className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="font-semibold text-slate-600">{d.machine_name}</p>
                          <p className="text-xs text-slate-500">
                            {d.start_date} - {d.end_date} • {d.total_days} days
                            {d.end_reason && ` • ${d.end_reason.replace('_', ' ')}`}
                          </p>
                        </div>
                      </div>
                      <Badge variant="default">Ended</Badge>
                    </div>
                  ))}
                  {deploymentHistory.length === 0 && (
                    <p className="text-sm text-slate-500 py-2">No deployment history for this job</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ) : showRecords ? (
        /* Job Records Panel — receipts/forms/dockets/photos for this job */
        <JobRecordsPanel
          job={job}
          data={jobRecords}
          loading={loadingRecords}
          onRefresh={fetchJobRecords}
          onBack={() => setShowRecords(false)}
        />
      ) : showReport && canViewReport ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold uppercase">Job Report - {job?.title}</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowReport(false)}>Back</Button>
          </div>

          {jobReport ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-orange-50 border-orange-200 text-center py-4">
                  <p className="text-3xl font-bold text-orange-600">{jobReport.totals.total_labour_hours}</p>
                  <p className="text-xs text-slate-600 uppercase">Total Labour Hours</p>
                </Card>
                <Card className="bg-blue-50 border-blue-200 text-center py-4">
                  <p className="text-3xl font-bold text-blue-600">{jobReport.totals.total_workers}</p>
                  <p className="text-xs text-slate-600 uppercase">Workers</p>
                </Card>
                <Card className="bg-green-50 border-green-200 text-center py-4">
                  <p className="text-3xl font-bold text-green-600">{jobReport.totals.workers_on_site_now}</p>
                  <p className="text-xs text-slate-600 uppercase">On Site Now</p>
                </Card>
                <Card className="bg-purple-50 border-purple-200 text-center py-4">
                  <p className="text-3xl font-bold text-purple-600">{jobReport.totals.machines_count}</p>
                  <p className="text-xs text-slate-600 uppercase">Machines</p>
                </Card>
              </div>

              {/* Currently On Site */}
              {jobReport.currently_on_site.length > 0 && (
                <Card className="bg-green-50 border-green-200">
                  <h4 className="font-bold uppercase text-sm mb-3 flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    Currently On Site ({jobReport.currently_on_site.length})
                  </h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    {jobReport.currently_on_site.map(worker => (
                      <div key={worker.user_id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                        <div>
                          <p className="font-semibold">{worker.user_name}</p>
                          <p className="text-xs text-slate-500">Since {new Date(worker.clock_in).toLocaleTimeString()}</p>
                        </div>
                        <Badge variant="success">{worker.hours_so_far}h</Badge>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Workers Summary */}
              <Card>
                <h4 className="font-bold uppercase text-sm mb-4">Worker Hours Summary</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-3 font-bold">Worker</th>
                        <th className="text-left p-3 font-bold">Role</th>
                        <th className="text-center p-3 font-bold">Entries</th>
                        <th className="text-center p-3 font-bold">First Day</th>
                        <th className="text-center p-3 font-bold">Last Day</th>
                        <th className="text-right p-3 font-bold">Hours on Job</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobReport.workers.map(worker => (
                        <tr key={worker.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-3 font-medium">{worker.user_name}</td>
                          <td className="p-3 text-slate-600 capitalize">{worker.user_role?.replace('_', ' ')}</td>
                          <td className="p-3 text-center">{worker.entries.length}</td>
                          <td className="p-3 text-center text-slate-600">
                            {worker.first_clock_in ? new Date(worker.first_clock_in).toLocaleDateString() : '-'}
                          </td>
                          <td className="p-3 text-center text-slate-600">
                            {worker.last_clock_out ? new Date(worker.last_clock_out).toLocaleDateString() : '-'}
                          </td>
                          <td className="p-3 text-right font-bold text-orange-600">{worker.total_hours.toFixed(1)}h</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-100 font-bold">
                      <tr>
                        <td colSpan="5" className="p-3">TOTAL</td>
                        <td className="p-3 text-right text-orange-600">{jobReport.totals.total_labour_hours}h</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Card>

              {/* Machines on Job */}
              {jobReport.machines.length > 0 && (
                <Card>
                  <h4 className="font-bold uppercase text-sm mb-4">Machines</h4>
                  <div className="space-y-3">
                    {jobReport.machines.map(machine => (
                      <div key={machine.machine_id} className={`flex items-center justify-between p-3 rounded-lg ${machine.is_hired ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                        <div>
                          <div className="flex items-center gap-2">
                            <Truck className={`w-5 h-5 ${machine.is_hired ? 'text-amber-600' : 'text-green-600'}`} />
                            <span className="font-semibold">{machine.machine_name}</span>
                            <Badge variant={machine.is_hired ? "warning" : "success"} className="text-xs">
                              {machine.is_hired ? 'Hired' : 'Owned'}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {machine.machine_type?.replace('_', ' ')}
                            {machine.is_hired && machine.hire_company && ` • ${machine.hire_company}`}
                          </p>
                        </div>
                        <div className="text-right">
                          {machine.total_hours > 0 && (
                            <p className="font-bold text-orange-600">{machine.total_hours}h logged</p>
                          )}
                          {machine.is_hired && machine.daily_rate && (
                            <p className="text-xs text-slate-500">${machine.daily_rate}/day</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Job Status */}
              <Card className="bg-slate-50">
                <h4 className="font-bold uppercase text-sm mb-3">Job Details</h4>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Client</p>
                    <p className="font-medium">{jobReport.job.client_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Location</p>
                    <p className="font-medium">{jobReport.job.location}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Status</p>
                    <Badge variant={jobReport.job.status === 'completed' ? 'success' : 'info'}>{jobReport.job.status}</Badge>
                  </div>
                  <div>
                    <p className="text-slate-500">Start Date</p>
                    <p className="font-medium">{jobReport.job.start_date || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">End Date</p>
                    <p className="font-medium">{jobReport.job.end_date || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Total Entries</p>
                    <p className="font-medium">{jobReport.totals.total_time_entries}</p>
                  </div>
                </div>
              </Card>
            </>
          ) : (
            <div className="text-center py-12">
              <Spinner size="lg" />
              <p className="mt-4 text-slate-500">Loading report...</p>
            </div>
          )}
        </div>
      ) : !showAssign && !showDocuments && !showRoster && !showSigningDocs && !showFormAllocations && !showForms ? (
        <>
          {/* Site Crew panel — only for existing jobs, admin/supervisor/manager see live crew + can clock others on/off */}
          {job?.id && ['admin', 'manager', 'supervisor'].includes(user?.role) && (
            <div className="mb-6">
              <SiteCrewPanel jobId={job.id} />
            </div>
          )}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Input
              label="Job Title"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              required
              disabled={!canEdit}
              data-testid="job-title-input"
            />
            {clients.length > 0 ? (
              <Select
                label="Client"
                value={formData.client_id}
                onChange={(e) => {
                  const client = clients.find(c => c.id === e.target.value);
                  setFormData({
                    ...formData, 
                    client_id: e.target.value,
                    client_name: client?.name || ""
                  });
                }}
                options={[
                  { value: "", label: "-- Select Client --" },
                  ...clients.map(c => ({ value: c.id, label: c.name }))
                ]}
                disabled={!canEdit}
                data-testid="job-client-select"
              />
            ) : (
              <Input
                label="Client Name"
                value={formData.client_name}
                onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                required
                disabled={!canEdit}
              />
            )}
          </div>

          <Input
            label="Location"
            value={formData.location}
            onChange={(e) => setFormData({...formData, location: e.target.value})}
            required
            disabled={!canEdit}
          />

          <div className="grid md:grid-cols-2 gap-6">
            <DatePicker
              label="Start Date"
              value={formData.start_date}
              onChange={(v) => setFormData({...formData, start_date: v})}
              required
              disabled={!canEdit}
              testId="job-start-date"
            />
            <DatePicker
              label="End Date"
              value={formData.end_date}
              onChange={(v) => setFormData({...formData, end_date: v})}
              minDate={formData.start_date ? new Date(formData.start_date) : undefined}
              disabled={!canEdit}
              testId="job-end-date"
            />
          </div>

          <Textarea
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            disabled={!canEdit}
          />

          <Textarea
            label="Job Plan"
            value={formData.job_plan}
            onChange={(e) => setFormData({...formData, job_plan: e.target.value})}
            placeholder="Phase 1: ...\nPhase 2: ..."
            disabled={!canEdit}
          />

          {/* Equipment Schedule Section */}
          {canEdit && (
            <EquipmentScheduleSection 
              schedule={formData.equipment_schedule}
              onChange={(schedule) => setFormData({...formData, equipment_schedule: schedule})}
            />
          )}

          {/* Display equipment schedule for non-editors */}
          {!canEdit && formData.equipment_schedule?.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-bold uppercase text-slate-600">Equipment Schedule</label>
              {formData.equipment_schedule.map((item, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-lg border">
                  <p className="font-semibold">{item.quantity}x {item.equipment_name}</p>
                  <p className="text-sm text-slate-500">{item.start_date} {item.end_date ? `- ${item.end_date}` : ''}</p>
                  {item.notes && <p className="text-sm text-slate-400">{item.notes}</p>}
                </div>
              ))}
            </div>
          )}

          <Input
            label="Safety Requirements (comma separated)"
            value={formData.safety_requirements.join(", ")}
            onChange={(e) => setFormData({...formData, safety_requirements: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})}
            disabled={!canEdit}
          />

          {/* Contract Amount — visible to admin + office only (canEdit guard covers this) */}
          {['admin', 'office_staff'].includes(user?.role) && (
            <div>
              <Input
                label="Contract Amount ($) — agreed total job value"
                type="number"
                step="0.01"
                min="0"
                value={formData.contract_amount}
                onChange={(e) => setFormData({...formData, contract_amount: e.target.value})}
                placeholder="e.g. 120000"
                disabled={!canEdit}
                data-testid="job-contract-amount-input"
              />
              <p className="text-xs text-slate-500 mt-1">Used on the Invoicing page to track progress payments, variations, and outstanding balance.</p>
            </div>
          )}

          {canEdit && (
            <Select
              label="Status"
              value={formData.status}
              onChange={(e) => setFormData({...formData, status: e.target.value})}
              options={[
                { value: "scheduled", label: "Scheduled" },
                { value: "in_progress", label: "In Progress" },
                { value: "completed", label: "Completed" },
                { value: "cancelled", label: "Cancelled" }
              ]}
            />
          )}

          {canEdit && (
            <Select
              label="Job Manager"
              value={formData.manager_id}
              onChange={(e) => setFormData({...formData, manager_id: e.target.value})}
              options={[
                { value: "", label: "No manager assigned" },
                ...users.filter(u => ['admin', 'manager', 'supervisor', 'office_staff'].includes(u.role)).map(u => ({
                  value: u.id,
                  label: `${u.first_name} ${u.last_name} (${u.role.replace('_', ' ')})`
                }))
              ]}
            />
          )}

          {!canEdit && job?.manager_name && (
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-bold uppercase text-slate-500">Job Manager</p>
              <p className="text-lg font-semibold">{job.manager_name}</p>
            </div>
          )}

          {/* GPS Geofencing Section */}
          {canEdit && (
            <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="gps_required"
                  checked={formData.gps_required}
                  onChange={(e) => setFormData({...formData, gps_required: e.target.checked})}
                  className="w-5 h-5"
                />
                <label htmlFor="gps_required" className="font-bold uppercase text-blue-800">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Require GPS to Clock In/Out
                </label>
              </div>
              
              {formData.gps_required && (
                <div className="grid md:grid-cols-3 gap-4 mt-4">
                  <Input
                    label="Site Latitude"
                    type="number"
                    step="any"
                    value={formData.site_latitude}
                    onChange={(e) => setFormData({...formData, site_latitude: e.target.value})}
                    placeholder="-33.8688"
                  />
                  <Input
                    label="Site Longitude"
                    type="number"
                    step="any"
                    value={formData.site_longitude}
                    onChange={(e) => setFormData({...formData, site_longitude: e.target.value})}
                    placeholder="151.2093"
                  />
                  <Input
                    label="Radius (meters)"
                    type="number"
                    value={formData.geofence_radius}
                    onChange={(e) => setFormData({...formData, geofence_radius: parseInt(e.target.value) || 100})}
                    placeholder="100"
                  />
                </div>
              )}
              <p className="text-xs text-blue-600">
                {formData.gps_required 
                  ? "Workers must be within the specified radius of the site coordinates to clock in."
                  : "Enable to require workers to be on-site to clock in."}
              </p>
            </div>
          )}

          {/* Show GPS status for non-editors */}
          {!canEdit && job?.gps_required && (
            <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <p className="font-bold uppercase text-blue-800">
                <MapPin className="w-4 h-4 inline mr-2" />
                GPS Required for this Job
              </p>
              <p className="text-sm text-blue-600 mt-1">
                You must be within {job.geofence_radius || 100}m of the site to clock in.
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-4">
            {canEdit && (
              <>
                <Button type="submit" disabled={loading} data-testid="save-job-btn">
                  {loading ? <Spinner size="sm" /> : (job ? "Update Job" : "Create Job")}
                </Button>
                {job && (
                  <>
                    <Button type="button" variant="outline" onClick={() => setShowAssign(true)}>
                      <Users className="w-5 h-5 mr-2" />
                      Workers
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowMachines(true)} data-testid="job-machines-btn">
                      <Truck className="w-5 h-5 mr-2" />
                      Equipment ({activeDeployments.length})
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowRoster(true)}>
                      <Calendar className="w-5 h-5 mr-2" />
                      Daily Roster
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowDocuments(true)}>
                      <FileText className="w-5 h-5 mr-2" />
                      Docs
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowSigningDocs(true)} data-testid="signing-docs-btn">
                      <FileText className="w-5 h-5 mr-2" />
                      Signing Docs ({signingDocuments.length})
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowFormAllocations(true)} data-testid="form-allocations-btn">
                      <ClipboardList className="w-5 h-5 mr-2" />
                      Form Library ({formAllocations.length})
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowSWMS(true)} data-testid="manage-swms-btn">
                      <Shield className="w-5 h-5 mr-2" />
                      SWMS
                    </Button>
                    <Button type="button" variant="outline" onClick={() => { setShowRecords(true); fetchJobRecords(); }} data-testid="job-records-btn">
                      <Receipt className="w-5 h-5 mr-2" />
                      Records
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="border-orange-500 text-orange-700 hover:bg-orange-50"
                      onClick={() => { onClose(); navigate(`/jobs/${job.id}/build`); }}
                      data-testid="job-build-workflow-btn"
                    >
                      <HardHat className="w-5 h-5 mr-2" />
                      Build Workflow
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowEquipReg(true)} data-testid="job-equipment-btn">
                      <Box className="w-5 h-5 mr-2" />
                      Equipment
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowElecReg(true)} data-testid="job-electrical-btn">
                      <Zap className="w-5 h-5 mr-2" />
                      Electrical
                    </Button>
                    {canViewReport && (
                      <Button type="button" variant="outline" onClick={() => { setShowReport(true); fetchJobReport(); }}>
                        <BarChart3 className="w-5 h-5 mr-2" />
                        Report
                      </Button>
                    )}
                  </>
                )}
              </>
            )}
            {job && !canEdit && (
              <>
                <Button type="button" variant="outline" onClick={() => setShowMachines(true)}>
                  <Truck className="w-5 h-5 mr-2" />
                  View Machines
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowDocuments(true)}>
                  <Eye className="w-5 h-5 mr-2" />
                  View Documents
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForms(true)} data-testid="view-forms-btn">
                  <FileText className="w-5 h-5 mr-2" />
                  Forms ({jobForms.total})
                </Button>
                <Button type="button" variant="outline" onClick={() => { setShowRecords(true); fetchJobRecords(); }} data-testid="job-records-btn-viewer">
                  <Receipt className="w-5 h-5 mr-2" />
                  Records
                </Button>
                {canViewReport && (
                  <Button type="button" variant="outline" onClick={() => { setShowReport(true); fetchJobReport(); }}>
                    <BarChart3 className="w-5 h-5 mr-2" />
                    Report
                  </Button>
                )}
                {swmsStatus?.has_swms && (
                  <Button 
                    type="button" 
                    variant={swmsStatus.signed ? "secondary" : "primary"}
                    onClick={() => setShowSignSWMS(true)}
                    data-testid="view-swms-btn"
                  >
                    <Shield className="w-5 h-5 mr-2" />
                    {swmsStatus.signed ? "View SWMS" : "Sign SWMS"}
                  </Button>
                )}
              </>
            )}
            <Button type="button" variant="ghost" onClick={onClose}>Close</Button>
          </div>

          {/* Complete / Cancel / Delete Job Actions */}
          {job && canEdit && (
            <div className="mt-6 pt-6 border-t-2 border-slate-200">
              <h4 className="font-bold uppercase text-slate-500 mb-4">Job Actions</h4>
              <div className="flex flex-wrap gap-3">
                {job.status !== 'completed' && job.status !== 'cancelled' && (
                  <>
                    <Button 
                      type="button" 
                      variant="secondary"
                      onClick={handleCompleteJob}
                      data-testid="complete-job-btn"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Complete Job
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={handleCancelJob}
                      data-testid="cancel-job-btn"
                    >
                      <X className="w-5 h-5 mr-2" />
                      Cancel Job
                    </Button>
                  </>
                )}
                <Button 
                  type="button" 
                  variant="destructive"
                  onClick={handleDeleteJob}
                  data-testid="delete-job-btn"
                >
                  <Trash2 className="w-5 h-5 mr-2" />
                  Delete Job
                </Button>
                {job.status === 'completed' && canViewReport && (
                  <Button 
                    type="button" 
                    variant="secondary"
                    onClick={fetchJobReport}
                    data-testid="view-report-btn"
                  >
                    <FileText className="w-5 h-5 mr-2" />
                    View Report
                  </Button>
                )}
              </div>
              {(job.status === 'completed' || job.status === 'cancelled') && (
                <p className="text-sm text-slate-500 mt-3">
                  This job is {job.status}. You can change the status using the dropdown above or delete it.
                </p>
              )}
            </div>
          )}

          {/* Job Uploads Section - visible for all users on existing jobs */}
          {job && (
            <div className="mt-6 pt-6 border-t-2 border-slate-200">
              <JobUploadsSection jobId={job.id} jobTitle={job.title} />
            </div>
          )}
        </form>
        </>
      ) : showAssign ? (
        <div className="space-y-4">
          <h3 className="text-lg font-bold uppercase">Assign Workers to Job</h3>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {users.map(user => (
              <label 
                key={user.id}
                className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                  assignedUsers.includes(user.id) ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={assignedUsers.includes(user.id)}
                  onChange={() => toggleUser(user.id)}
                  className="w-5 h-5"
                />
                <div>
                  <p className="font-semibold">{user.first_name} {user.last_name}</p>
                  <p className="text-sm text-slate-500 uppercase">{user.role?.replace('_', ' ')}</p>
                </div>
              </label>
            ))}
          </div>
          <div className="flex gap-4">
            <Button onClick={handleAssign}>Save Assignments</Button>
            <Button variant="ghost" onClick={() => setShowAssign(false)}>Back</Button>
          </div>
        </div>
      ) : showDocuments ? (
        <DocumentsView 
          job={job}
          documents={documents}
          onRefresh={fetchDocuments}
          canEdit={canEdit}
          onBack={() => setShowDocuments(false)}
        />
      ) : showSigningDocs ? (
        <SigningDocumentsView
          job={job}
          documents={signingDocuments}
          users={users}
          onRefresh={fetchSigningDocuments}
          canEdit={canEdit}
          onBack={() => setShowSigningDocs(false)}
        />
      ) : showFormAllocations ? (
        <div className="space-y-4" data-testid="form-allocations-view">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold uppercase flex items-center gap-2">
              <ClipboardList className="w-5 h-5" /> Form Library — {job?.title}
            </h3>
            <div className="flex gap-2">
              {canEdit && (
                <Button size="sm" onClick={() => setShowAddAllocation(true)} data-testid="add-form-allocation-btn">
                  <Plus className="w-4 h-4 mr-1" /> Allocate Form
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => setShowFormAllocations(false)}>Back</Button>
            </div>
          </div>
          <p className="text-xs text-slate-500">Allocate pre-made fillable forms (Toolbox Talk, Weekly Report, Incident Report, etc.) to this job. Workers get prompted to fill + sign at the chosen frequency.</p>

          {showAddAllocation && canEdit && (
            <Card className="bg-blue-50 border-2 border-blue-200 space-y-3" data-testid="add-allocation-form">
              <h4 className="font-bold uppercase text-sm">Allocate a Form</h4>
              <Select
                label="Form Template"
                value={newAllocation.template_id}
                onChange={(e) => {
                  const tplId = e.target.value;
                  const tpl = formTemplates.find(t => t.id === tplId);
                  // Smart default: incident reports → each_person_fills; everything else → supervisor_fills_team_signs
                  const cat = (tpl?.category || '').toLowerCase();
                  const smartMode = cat.includes('incident') ? 'each_person_fills' : 'supervisor_fills_team_signs';
                  setNewAllocation({ ...newAllocation, template_id: tplId, completion_mode: smartMode });
                }}
                options={[{ value: '', label: 'Select a form…' }, ...formTemplates.map(t => ({ value: t.id, label: `${t.name}${t.category ? ` (${t.category})` : ''}` }))]}
                testId="allocation-template-select"
              />
              <Select
                label="Who fills this form?"
                value={newAllocation.completion_mode}
                onChange={(e) => setNewAllocation({ ...newAllocation, completion_mode: e.target.value })}
                options={[
                  { value: 'supervisor_fills_team_signs', label: 'Supervisor fills it once — crew sign to acknowledge (SWMS, Toolbox, Pre-Start)' },
                  { value: 'each_person_fills', label: 'Each allocated person fills their own (Incident reports, Personal SWMS)' }
                ]}
                testId="allocation-completion-mode-select"
              />
              <p className="text-xs text-slate-500 -mt-1 px-1">
                {newAllocation.completion_mode === 'each_person_fills'
                  ? 'Every required worker submits their own copy of the form. Use for incident reports.'
                  : 'Supervisor fills the form once. Each required worker just signs an "I have read & understood" line.'}
              </p>
              <Select
                label="When to fill"
                value={newAllocation.signing_frequency}
                onChange={(e) => setNewAllocation({ ...newAllocation, signing_frequency: e.target.value })}
                options={[
                  { value: 'first_clock_in', label: 'First Clock-in (start of job)' },
                  { value: 'daily', label: 'Daily (every clock-in)' },
                  { value: 'weekly_start', label: 'Weekly — Start of week (Mon)' },
                  { value: 'weekly_end', label: 'Weekly — End of week (Sun)' },
                  { value: 'end_of_job', label: 'End of Job (required to mark complete)' },
                  { value: 'once', label: 'Once only' }
                ]}
                testId="allocation-frequency-select"
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={newAllocation.all_employees_must_sign}
                  onChange={(e) => setNewAllocation({ ...newAllocation, all_employees_must_sign: e.target.checked })}
                />
                All clocked-in employees must fill this form
              </label>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowAddAllocation(false)}>Cancel</Button>
                <Button size="sm" onClick={addFormAllocation} data-testid="submit-allocation">Allocate</Button>
              </div>
            </Card>
          )}

          {formAllocations.length === 0 ? (
            <Card className="text-center py-8">
              <p className="text-slate-500">No forms allocated yet. Click "Allocate Form" to get started.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {formAllocations.map(a => {
                const isEachFills = a.completion_mode === 'each_person_fills';
                const statusVariant = a.status === 'complete' ? 'success'
                  : a.status === 'awaiting_supervisor' ? 'warning'
                  : a.status === 'awaiting_signatures' ? 'warning'
                  : a.status === 'in_progress' ? 'info' : 'default';
                const statusLabel = ({
                  complete: 'Complete',
                  awaiting_supervisor: 'Awaiting supervisor to fill',
                  awaiting_signatures: 'Awaiting crew signatures',
                  in_progress: 'In progress',
                  pending: 'Pending'
                })[a.status] || a.status;
                return (
                  <Card key={a.id} className="flex items-start justify-between gap-3" data-testid={`allocation-${a.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-bold">{a.template_name}</h4>
                        <Badge variant="info">{(a.signing_frequency || 'once').replace(/_/g, ' ').toUpperCase()}</Badge>
                        {a.template_category && <Badge variant="secondary">{a.template_category}</Badge>}
                        <Badge variant={isEachFills ? 'warning' : 'default'}>
                          {isEachFills ? 'Each person fills' : 'Supervisor fills, crew signs'}
                        </Badge>
                        {a.all_employees_must_sign && <Badge variant="warning">All crew</Badge>}
                        <Badge variant={statusVariant}>{statusLabel}</Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {isEachFills ? (
                          <>
                            <strong>{a.signed_count || 0}</strong>{a.target_user_ids?.length ? ` / ${a.target_user_ids.length}` : ''} crew submitted
                            {a.pending_count > 0 && <span className="text-amber-600"> · {a.pending_count} pending</span>}
                          </>
                        ) : (
                          <>
                            {a.primary_submission_id ? (
                              <>Filled by <strong>{a.primary_submitted_by || 'supervisor'}</strong>. </>
                            ) : (
                              <span className="text-amber-600">Not filled yet. </span>
                            )}
                            <strong>{a.signed_count || 0}</strong>{a.target_user_ids?.length ? ` / ${a.target_user_ids.length}` : ''} crew signed
                            {a.pending_count > 0 && <span className="text-amber-600"> · {a.pending_count} pending</span>}
                          </>
                        )}
                        <span className="text-slate-400"> · Allocated by {a.created_by_name}</span>
                      </p>
                    </div>
                    {canEdit && (
                      <Button variant="ghost" size="sm" onClick={() => removeFormAllocation(a.id)} data-testid={`remove-allocation-${a.id}`}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      ) : showForms ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold uppercase flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Job Forms - {job?.title}
            </h3>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => {
                  // Cache target job so FormsPage pre-binds it
                  try { localStorage.setItem('shadowops_fill_form_target_job', JSON.stringify({ id: job.id, title: job.title })); } catch (e) {}
                  window.location.href = '/forms?fill_for_job=' + encodeURIComponent(job.id);
                }}
                data-testid="fill-new-form-for-job-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Fill New Form
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowForms(false)}>Back</Button>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-700">{jobForms.total}</p>
              <p className="text-xs text-blue-600 uppercase">Total Forms</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-lg text-center">
              <p className="text-2xl font-bold text-amber-700">{jobForms.pending_signatures || 0}</p>
              <p className="text-xs text-amber-600 uppercase">Pending Signatures</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <p className="text-2xl font-bold text-purple-700">{jobForms.pending_approval || 0}</p>
              <p className="text-xs text-purple-600 uppercase">Awaiting Approval</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-700">{jobForms.approved || 0}</p>
              <p className="text-xs text-green-600 uppercase">Approved</p>
            </div>
          </div>

          {/* Forms List */}
          {jobForms.submissions?.length === 0 ? (
            <div className="text-center py-12 text-slate-500 bg-slate-50 rounded-lg">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="font-semibold">No forms linked to this job yet</p>
              <p className="text-sm">Forms submitted with this job selected will appear here</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {jobForms.submissions?.map(form => (
                <div key={form.id} className="p-4 border-2 rounded-lg bg-white space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-bold">{form.template_name}</h4>
                      <p className="text-sm text-slate-500">Submitted by {form.user_name}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant={
                        form.approval_status === 'approved' ? 'success' : 
                        form.signature_status === 'complete' ? 'warning' : 'default'
                      }>
                        {form.approval_status === 'approved' ? 'Approved' :
                         form.signature_status === 'complete' ? 'Needs Approval' :
                         form.signature_status === 'partial' ? 'Signatures Pending' : 'Waiting for Signatures'}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>{new Date(form.submitted_at).toLocaleString()}</span>
                    {form.involved_employees?.length > 0 && (
                      <span>{form.involved_employees.length} people involved</span>
                    )}
                  </div>
                  {form.required_signatures?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {form.required_signatures.map(sig => (
                        <span 
                          key={sig.id} 
                          className={`text-xs px-2 py-1 rounded ${sig.signed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}
                        >
                          {sig.name} {sig.signed ? '✓' : '(pending)'}
                        </span>
                      ))}
                    </div>
                  )}
                  {form.approval_status === 'pending' && form.signature_status === 'complete' && canEdit && (
                    <Button 
                      size="sm" 
                      variant="secondary" 
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/forms/submissions/${form.id}/approve`);
                          alert('Form approved!');
                          fetchJobForms();
                        } catch (e) {
                          alert(e.response?.data?.detail || 'Failed to approve');
                        }
                      }}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve Form
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : showRoster ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold uppercase">Daily Roster - {job?.title}</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowRoster(false)}>Back</Button>
          </div>
          <DailyRosterSection jobId={job?.id} jobTitle={job?.title} />
        </div>
      ) : (
        <div className="space-y-4">
          <h3 className="text-lg font-bold uppercase">Assign Workers to Job</h3>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {users.map(user => (
              <label 
                key={user.id}
                className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                  assignedUsers.includes(user.id) ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={assignedUsers.includes(user.id)}
                  onChange={() => toggleUser(user.id)}
                  className="w-5 h-5"
                />
                <div>
                  <p className="font-semibold">{user.first_name} {user.last_name}</p>
                  <p className="text-sm text-slate-500 uppercase">{user.role?.replace('_', ' ')}</p>
                </div>
              </label>
            ))}
          </div>
          <div className="flex gap-4">
            <Button onClick={handleAssign}>Save Assignments</Button>
            <Button variant="ghost" onClick={() => setShowAssign(false)}>Back</Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default JobModal;
